import { defineStore, acceptHMRUpdate } from 'pinia';
import Planning from '@/models/Planning';
import ProjectSrv from '@/components/Projects/ProjectSrv';
import usePlanningStore from './planning';
import useUndoRedoStore from '@/stores/undoredo';
import usePlanningElementsStore from './elements';

let undoState = {};

function undoredoConfig() {
  const planningStore = usePlanningStore();
  const { planning } = planningStore;
  if (angular.equals(planning.config, undoState)) return;
  const newState = angular.copy(planning.config);
  const oldState = angular.copy(undoState);

  useUndoRedoStore().add({
    action: () => {
      planning.config.set(angular.copy(newState));
      planning.config.setDesign();
    },
    rollback: () => {
      planning.config.set(angular.copy(oldState));
      planning.config.setDesign();
    },
  });
  planningStore.save();
}

const store = defineStore('planning-config', {
  actions: {
    init() {
      const { planning } = usePlanningStore();
      planning.config.setDesign();
    },
    set(newVals) {
      const { planning } = usePlanningStore();
      planning.config.set(newVals);
      planning.config.setDesign();
    },
    async updatePlanningRecurrences() {
      const { planning } = usePlanningStore();
      const recurrences = Object.values(planning.config.recurrences || {});
      if (! recurrences.length) return Promise.resolve(null);
      let configChanged = false;
      const results = [];
      const elementsByRecurrenceId = _.groupBy(planning.elements, el => el.getRecurrenceId());
      recurrences.forEach((recurrence) => {
        const recurrenceElements = elementsByRecurrenceId[recurrence.id];
        if (! recurrenceElements?.length) {
          delete planning.config.recurrences[recurrence.id];
          configChanged = true;
          return;
        }
        const referenceElement = _.max(recurrenceElements, el => el.getStartTime());
        results.push(this.generateRecurrenceElements({ recurrence, referenceElement }));
      });
      if (_.isEmpty(planning.config.recurrences)) planning.config.recurrences = null;
      return configChanged || results.some(item => item.length); // return true if need to save
    },
    generateRecurrenceElements({ recurrence, referenceElement, contextDate }) {
      if (! referenceElement.hasDates()) return Promise.resolve([]);
      const maxElements = 20; // arbitrary safe limit
      const templateContent = {
        elements: [{ ...referenceElement.getAll(), dependencies: null, recurrence_id: recurrence.id }],
      };

      // when updating, limit to 'occurrences' elements in the future (contextDate = undefined)
      // when creating a new recurrence, create at least 'occurrences' elements, even if el is in the future
      let limitDate = moment.max(moment(), moment(contextDate)).add(recurrence.occurrences * recurrence.interval, recurrence.frequency);
      if (recurrence.limitDate) limitDate = moment.min(limitDate, moment(recurrence.limitDate));

      const newElements = [];
      if (recurrence.frequency != 'months' || recurrence.monthlyOption == 'dayOfMonth') {
        let startdate;
        for (let i = 0; i < maxElements; i++) {
          startdate = referenceElement.getStartTime().add((i + 1) * recurrence.interval, recurrence.frequency); // this way handles 'every 31th'
          if (startdate.isAfter(limitDate)) break;
          const newel = ProjectSrv.projectFromTemplate({ template: templateContent, startdate });
          newElements.push(...newel.elements);
        }
      } else { // monthlyOption == 'dayOfWeek'
        const dayInfos = { week: Math.ceil(referenceElement.getStartTime().format('D') / 7), day: referenceElement.getStartTime().isoWeekday() };
        for (let i = 0; i < maxElements; i++) {
          let startdate;
          const nextMonth = referenceElement.getStartTime().add((i + 1) * recurrence.interval, 'months');
          let inc = dayInfos.day;
          if (dayInfos.week <= 4) { // original month contains at most 4 weeks
            const monthFirstDay = nextMonth.startOf('month');
            if (monthFirstDay.isoWeekday() > dayInfos.day) {
              inc += 7;
            }
            inc += (dayInfos.week - 1) * 7;
            monthFirstDay.isoWeekday(inc);
            startdate = monthFirstDay;
          } else { // original month contains 5 weeks
            const monthLastDay = nextMonth.endOf('month');
            if (monthLastDay.isoWeekday() < dayInfos.day) {
              inc -= 7;
            }
            monthLastDay.isoWeekday(inc);
            startdate = monthLastDay.startOf('day');
          }
          if (startdate.isAfter(limitDate)) break;
          const newel = ProjectSrv.projectFromTemplate({ template: templateContent, startdate });
          newElements.push(...newel.elements);
        }
      }

      const templatePlanning = new Planning({ elements: newElements });

      const argstab = templatePlanning.elements.map(templateEl => ({ type: templateEl.getType(), lane: { id: templateEl.getLaneId() }, el: templateEl }));
      return usePlanningElementsStore().addElements({ argstab, silent: true });
    },
    startChangingConfig() {
      const { planning } = usePlanningStore();
      undoState = angular.copy(planning.config);
    },
    changingConfig() {
      const { planning } = usePlanningStore();
      if (! angular.equals(planning.config, undoState)) {
        undoredoConfig();
      }
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
